import React, { useState, useEffect, memo, useRef } from "react";
import { motion, useMotionValue, useTransform, animate, useInView } from "framer-motion";
import { 
  Map, Camera, MapIcon, ChevronRight, 
  Smartphone, Globe, Gamepad2, Headset, Sparkles, Route, Headphones, Wallet
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import NearbyUserModal from "./NearbyUserPage";
import logo from "./logo.png";

const API_URL = process.env.REACT_APP_API_URL;

// --- SEO SCHEMA ---
const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Mekânname AI",
  "url": "https://mekanname.com",
  "description": "Yapay zeka ile tarihi mekanları tanı, keşfet ve rotalar oluştur.",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://mekanname.com/ara?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

// --- ANİMASYON VARYASYONLARI ---
const fadeInUpVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { 
      duration: 0.6, 
      delay: delay,
      ease: [0.22, 1, 0.36, 1] 
    }
  })
};

// --- ORTAK BAŞLIK BİLEŞENİ (ANİMASYONLU) ---
const SectionHeader = ({ title, highlight, description, align = "center", className = "" }) => (
  <motion.div 
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-50px" }}
    variants={fadeInUpVariant}
    className={`mb-12 ${align === "left" ? "text-left" : "text-center"} w-full ${className}`}
  >
    <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
      {title} <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-indigo-500">{highlight}</span>
    </h2>
    {description && (
      <p className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
        {description}
      </p>
    )}
  </motion.div>
);

// --- SECTION BİLEŞENİ ---
const Section = ({ children, className = "", id = "" }) => (
  <section id={id} className={`relative w-full flex justify-center px-4 sm:px-6 md:px-12 ${className}`}>
    <div className="relative z-0 w-full max-w-7xl">{children}</div>
  </section>
);

// --- SKELETON LOADER ---
const SkeletonRouteCard = () => (
    <div className="flex-none w-72 md:w-80 h-80 rounded-3xl bg-slate-800 animate-pulse border border-slate-700">
        <div className="h-48 bg-slate-700 w-full rounded-t-3xl"></div>
        <div className="p-6 space-y-3">
            <div className="h-6 bg-slate-700 rounded w-3/4"></div>
            <div className="h-4 bg-slate-700 rounded w-1/2"></div>
        </div>
    </div>
);

// --- RAKAM SAYDIRMA ANİMASYON BİLEŞENİ ---
const AnimatedCounter = ({ to, suffix = "", decimals = 0, duration = 2.5 }) => {
    const count = useMotionValue(0);
    const rounded = useTransform(count, (latest) => Number(latest).toFixed(decimals) + suffix);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });

    useEffect(() => {
        if (isInView) {
            const controls = animate(count, to, { duration: duration, ease: "easeOut" });
            return controls.stop;
        }
    }, [isInView, count, to, duration]);

    return <motion.span ref={ref}>{rounded}</motion.span>;
};

// --- KART BİLEŞENLERİ (ANİMASYONLU) ---

const StatisticCard = memo(({ title, to, suffix, decimals = 0, delay }) => (
  <motion.div 
    variants={fadeInUpVariant}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    custom={delay}
    className="bg-slate-800/50 rounded-2xl p-6 md:p-8 shadow-lg text-center border border-slate-700 hover:border-indigo-500 transition-colors duration-300 backdrop-blur-sm"
  >
    <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-indigo-400">
        <AnimatedCounter to={to} suffix={suffix} decimals={decimals} />
    </div>
    <h3 className="text-sm sm:text-lg text-gray-400 mt-2 font-medium">{title}</h3>
  </motion.div>
));

const HowItWorksCard = memo(({ icon: Icon, title, desc, colorClass, delay }) => (
    <motion.div 
        variants={fadeInUpVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        custom={delay}
        className="flex items-start gap-4 p-5 rounded-2xl bg-slate-800/40 border border-slate-700/50 hover:bg-slate-800 transition-all group"
    >
        <div className={`mt-1 p-3 rounded-xl ${colorClass.replace('text-', 'bg-')}/10 border border-slate-700 group-hover:scale-110 transition-transform`}>
            <Icon size={24} className={colorClass} />
        </div>
        <div>
            <h4 className="text-lg font-bold text-white mb-1 group-hover:text-teal-300 transition-colors">{title}</h4>
            <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
        </div>
    </motion.div>
));

const FutureCard = memo(({ icon, title, date, desc, delay }) => (
  <motion.div 
    variants={fadeInUpVariant}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    custom={delay}
    className="relative p-6 rounded-3xl border border-slate-700 bg-slate-800/50 hover:bg-slate-800 transition-all duration-300 group shadow-lg"
  >
    <div className="absolute top-4 right-4 bg-indigo-500/10 text-indigo-300 text-xs font-bold px-3 py-1 rounded-full border border-indigo-500/20">
      {date}
    </div>
    <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center text-teal-400 mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-teal-900/10 border border-slate-700">
      {icon}
    </div>
    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-teal-300 transition-colors">{title}</h3>
    <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
  </motion.div>
));

const RouteCard = memo(({ image, title, desc, id }) => (
    <motion.article
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
        className="flex-none w-72 md:w-80 snap-center rounded-3xl overflow-hidden shadow-2xl group relative bg-slate-800 border border-slate-700"
    >
        <Link to={`/rotalar/${id}`} aria-label={`${title} rotasını incele`}>
            <div className="relative h-48 md:h-56 w-full overflow-hidden">
                <img
                    src={image}
                    alt={`${title} tarihi gezi rotası`}
                    loading="lazy"
                    width="320"
                    height="224"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/600x400/0e1d2d/FFFFFF/png?text=Mekanname+Rota'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-90"></div>
            </div>
            <div className="p-5 md:p-6 bg-slate-800">
                <h3 className="text-xl md:text-2xl font-bold text-teal-300 truncate">{title}</h3>
                <p className="text-sm md:text-base text-gray-400 mt-2 line-clamp-2">{desc}</p>
                <div className="mt-4 flex items-center text-indigo-400 text-sm font-semibold group-hover:translate-x-2 transition-transform">
                    İncele <ChevronRight size={16} className="ml-1" />
                </div>
            </div>
        </Link>
    </motion.article>
));

export default function HomePage() {
  const navigate = useNavigate(); 
  const [modalOpen, setModalOpen] = useState(false);
  const [width, setWidth] = useState(0);
  const carouselRef = useRef();
  const [routes, setRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(true);
  const [routesError, setRoutesError] = useState(null);

  useEffect(() => {
    const fetchRoutes = async () => {
        try {
            const response = await axios.get(`${API_URL}/homepage`);
            setRoutes(response.data);
        } catch (error) {
            console.error("Rotalar çekilirken hata oluştu:", error);
            setRoutesError("Bağlantı hatası. Lütfen daha sonra tekrar deneyin.");
        } finally {
            setRoutesLoading(false);
        }
    };

    fetchRoutes();
  }, []);

  useEffect(() => {
    if (!routesLoading && carouselRef.current) {
        const updateWidth = () => {
            if(carouselRef.current) {
                setWidth(carouselRef.current.scrollWidth - carouselRef.current.offsetWidth);
            }
        };
        updateWidth();
        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }
  }, [routesLoading, routes]);

  return (
    <div className="bg-slate-950 font-sans text-white overflow-x-hidden">
      <Helmet>
        <title>Mekânname AI - Tarihi Fotoğrafla Keşfet</title>
        <link rel="canonical" href="https://mekanname.com/" />
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>

      {/* --- HERO SECTION (bg-950) --- */}
      <header className="relative min-h-screen flex items-center justify-center text-center px-4 sm:px-6 md:px-12 pt-20 bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/40 via-slate-950 to-slate-950 pointer-events-none"></div>
        
        <div className="relative z-10 w-full max-w-4xl mx-auto">
          <motion.div
            className="flex flex-col items-center justify-center gap-8"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div 
                className="relative group"
                whileHover={{ scale: 1.05 }}
            >
                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                <img
                    src={logo} 
                    alt="Mekânname Logo"
                    width="112" height="112"
                    className="relative w-24 md:w-28 h-24 md:h-28 rounded-full border-2 border-indigo-500/50 shadow-2xl object-cover"
                />
            </motion.div>

            <h1 className="text-5xl sm:text-6xl md:text-8xl font-extrabold tracking-tight">
              <span className="bg-gradient-to-r from-indigo-300 via-purple-300 to-indigo-300 bg-clip-text text-transparent">
                Mekânname
              </span>
              <span className="block text-2xl md:text-4xl mt-2 text-indigo-500 font-bold">AI Destekli Tarih Rehberi</span>
            </h1>

            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Fotoğrafını çek, yükle ve yapay zekanın senin için tarihi aralamasına izin ver. Geçmişin izlerini sürmeye hazır mısın?
            </p>

            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <Link to="/upload" className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full blur opacity-60 group-hover:opacity-100 transition duration-200"></div>
                <button className="relative flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 rounded-full transition-all duration-200 font-semibold text-lg border border-slate-700">
                  <Camera size={22} className="text-indigo-400" /> 
                  <span>Hemen Keşfet</span>
                </button>
              </Link>
              
              <button
                onClick={() => setModalOpen(true)}
                className="flex items-center gap-2 bg-slate-800/50 hover:bg-slate-800 border border-slate-700 hover:border-indigo-500/50 px-8 py-4 rounded-full text-lg font-semibold transition-all duration-200 backdrop-blur-sm"
              >
                <MapIcon size={22} className="text-teal-400" /> 
                <span>Yakınımı Göster</span>
              </button>
              <NearbyUserModal isOpen={modalOpen} onRequestClose={() => setModalOpen(false)} />
            </div>
          </motion.div>
        </div>
      </header>

      <main>
        {/* 1. NASIL ÇALIŞIR & YETENEKLER (bg-900) */}
        <Section className="py-24 bg-slate-900 border-t border-slate-800 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600/10 rounded-full blur-[100px] pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600/10 rounded-full blur-[100px] pointer-events-none"></div>

            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
                <motion.div 
                    className="w-full lg:w-1/2"
                    initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUpVariant}
                >
                    <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
                        Tarihi Keşfetmenin <br/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                            En Akıllı Yolu
                        </span>
                    </h2>
                    <p className="text-slate-400 text-lg leading-relaxed mb-8">
                        Mekânname, token (kredi) sistemiyle çalışan, yapay zeka destekli yeni nesil bir gezi asistanıdır. Karşılaştığın tarihi yapıları anında tanır ve senin için canlandırır.
                    </p>
                    
                    <button 
                        onClick={() => navigate('/help')}
                        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-8 py-4 rounded-xl transition-all shadow-lg shadow-indigo-600/20 hover:-translate-y-1"
                    >
                        Tüm Özellikleri Keşfet <ChevronRight size={20} />
                    </button>
                </motion.div>

                <div className="w-full lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <HowItWorksCard delay={0.1} icon={Camera} colorClass="text-blue-400" title="Fotoğraf Analizi" desc="Çektiğin yapıyı anında tespit et." />
                    <HowItWorksCard delay={0.2} icon={Sparkles} colorClass="text-purple-400" title="AI Yorumları" desc="Mekana edebi ve sanatsal bakış." />
                    <HowItWorksCard delay={0.3} icon={Headphones} colorClass="text-cyan-400" title="Sesli Hikayeler" desc="Seçtiğin tarzda tarihi dinle." />
                    <HowItWorksCard delay={0.4} icon={Route} colorClass="text-indigo-400" title="Gezi Asistanı" desc="Bütçene uygun akıllı rotalar çiz." />
                    <HowItWorksCard delay={0.5} icon={Gamepad2} colorClass="text-yellow-400" title="Oyunlaştırma" desc="Quizleri çöz, ücretsiz token kazan." />
                    <HowItWorksCard delay={0.6} icon={Wallet} colorClass="text-emerald-400" title="Token Sistemi" desc="Sadece kullandığın özellik kadar öde." />
                </div>
            </div>
        </Section>

        {/* 2. ROTALAR (bg-950) */}
        <Section className="py-24 bg-slate-950 border-t border-slate-800 relative">
            <div className="relative z-10 w-full">
                <motion.div 
                  initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUpVariant}
                  className="flex flex-col md:flex-row items-end justify-between w-full mb-10"
                >
                  <div className="w-full text-center md:text-left">
                    <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
                      Özel Keşif <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-indigo-500">Rotaları</span>
                    </h2>
                    <p className="text-slate-400 text-lg">Editörlerimizin seçtiği en popüler rotalar.</p>
                  </div>
                  
                  <Link to="/route" className="mt-6 md:mt-0 flex items-center text-indigo-400 hover:text-indigo-300 font-semibold transition-colors shrink-0 mx-auto md:mx-0">
                      Tümünü Gör <ChevronRight size={20} />
                  </Link>
                </motion.div>
                
                <div className="w-full overflow-hidden relative cursor-grab active:cursor-grabbing">
                    <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none md:hidden"></div>
                    
                    <motion.div
                        ref={carouselRef}
                        className="flex gap-6 pb-8 pl-2"
                        drag="x"
                        dragConstraints={{ left: -width, right: 0 }}
                        whileTap={{ cursor: "grabbing" }}
                    >
                        {routesLoading ? (
                            [...Array(4)].map((_, i) => <SkeletonRouteCard key={i} />)
                        ) : routesError ? (
                            <div className="text-red-400 p-4 border border-red-900 rounded bg-red-900/20">{routesError}</div>
                        ) : (
                            routes.map((rota) => (
                                <RouteCard key={rota.id} id={rota.id} image={rota.gorsel} title={rota.baslik} desc={rota.aciklama} />
                            ))
                        )}
                    </motion.div>
                </div>
            </div>
        </Section>

        {/* 3. GELECEĞİMİZ (bg-900) */}
        <Section className="py-24 bg-slate-900 border-t border-slate-800">
          <SectionHeader 
            title="Mekânname'nin" 
            highlight="Geleceği"
            description="Sadece bir rehber değil, yaşayan bir tarih platformu olma yolunda ilerliyoruz."
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FutureCard delay={0.1} icon={<Smartphone size={24} />} date="Faz 2" title="AR Deneyimi" desc="Telefon kameranızı tarihi yapıya tutun, Artırılmış Gerçeklik (AR) ile geçmişteki halini canlı görün." />
            <FutureCard delay={0.2} icon={<Globe size={24} />} date="Faz 3" title="Global Keşif" desc="Sadece Türkiye değil, Balkanlar ve Ortadoğu'daki Osmanlı eserleri de veri tabanımıza ekleniyor." />
            <FutureCard delay={0.3} icon={<Headset size={24} />} date="Faz 4" title="Akıllı Sesli Asistan" desc="Konum tabanlı sesli anlatım ile telefonunuzu cebinizden çıkarmadan tarihi dinleyerek öğrenin." />
            <FutureCard delay={0.4} icon={<Gamepad2 size={24} />} date="Yakında" title="Tarih Oyunlaştırma" desc="Mekanları keşfettikçe puan toplayın, rütbeler kazanın ve tarih kaşifleri liderlik tablosunda yükselin." />
          </div>
        </Section>

        {/* 4. İSTATİSTİKLER (bg-950) */}
        <Section className="py-24 pb-32 bg-slate-950 border-t border-slate-800">
            <div className="flex flex-col items-center">
                <SectionHeader 
                  title="Rakamlarla" 
                  highlight="Mekânname"
                  description="Her geçen gün genişleyen veri tabanımız ve büyüyen tarih sever topluluğumuzla geçmişi geleceğe taşıyoruz."
                />
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 w-full">
                    {/* AnimatedCounter kullanan dinamik sayılar */}
                    <StatisticCard title="Kayıtlı Mekân" to={270} suffix="+" delay={0.1} />
                    <StatisticCard title="Kullanıcı" to={1.5} suffix="K+" decimals={1} delay={0.2} />
                    <StatisticCard title="Deneyim Paylaşımı" to={4.3} suffix="K+" decimals={1} delay={0.3} />
                    <StatisticCard title="Keşif Rotası" to={820} suffix="+" delay={0.4} />
                </div>
            </div>
        </Section>
      </main>
    </div>
  );
}